import React, { useEffect, useRef, useState } from 'react';
import {
  ArrowRight, Mail, MapPin, Phone, Clock, Send,
  MessageSquare, Building2, Globe, Twitter, Linkedin,
  Github, HelpCircle, Zap
} from 'lucide-react';
import { Link } from 'react-router-dom';
import landingService from '../../../services/landingService';
import CompanyLayout from './CompanyLayout';
import './ContactPage.css';

const ContactPage = () => {
  const [visibleSections, setVisibleSections] = useState(new Set());
  const [formData, setFormData] = useState({
    name: '', email: '', subject: '', message: '',
  });
  const [submitStatus, setSubmitStatus] = useState({ message: '', type: '' });
  const [submitting, setSubmitting] = useState(false);
  const sectionRefs = useRef({});

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => new Set([...prev, entry.target.id]));
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -60px 0px' }
    );
    Object.values(sectionRefs.current).forEach((ref) => {
      if (ref) observer.observe(ref);
    });
    return () => observer.disconnect();
  }, []);

  const isVisible = (id) => visibleSections.has(id);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitStatus({ message: '', type: '' });
    const result = await landingService.submitContact(formData);
    setSubmitStatus({
      message: result.message || (result.success ? 'Thanks! We\'ll get back to you within 24 hours.' : 'Something went wrong. Please try again.'),
      type: result.success ? 'success' : 'error',
    });
    if (result.success) setFormData({ name: '', email: '', subject: '', message: '' });
    setSubmitting(false);
  };

  const contactChannels = [
    {
      icon: Mail,
      title: 'Email Us',
      detail: 'support@ResearchPro.io',
      sub: 'We reply within 24 hours',
    },
    {
      icon: MessageSquare,
      title: 'Live Chat',
      detail: 'Available 9am – 6pm EST',
      sub: 'Mon – Fri, real humans',
    },
    {
      icon: Phone,
      title: 'Phone',
      detail: '+1 (555) 012-3456',
      sub: 'For enterprise inquiries',
    },
  ];

  const offices = [
    {
      city: 'San Francisco',
      address: '100 Market Street, Suite 300\nSan Francisco, CA 94105',
      timezone: 'PST (UTC-8)',
    },
    {
      city: 'London',
      address: '45 King William Street\nLondon, EC4R 9AN, UK',
      timezone: 'GMT (UTC+0)',
    },
    {
      city: 'Singapore',
      address: '1 Raffles Place, Tower 2\nSingapore 048616',
      timezone: 'SGT (UTC+8)',
    },
  ];

  const socials = [
    { icon: Twitter, label: 'Twitter', href: '#' },
    { icon: Linkedin, label: 'LinkedIn', href: '#' },
    { icon: Github, label: 'GitHub', href: '#' },
  ];

  return (
    <CompanyLayout>
      {/* Hero */}
      <section className="rsc-hero contact-hero">
        <div className="rsc-hero-inner">
          <h1 className="rsc-hero-title">
            Get in Touch<br />
            <span className="rsc-hero-accent">We'd Love to Hear From You</span>
          </h1>
          <p className="rsc-hero-subtitle">
            Have a question, feedback, or partnership idea? Reach out and our team
            will get back to you as soon as possible.
          </p>
        </div>
      </section>

      {/* Contact Channels */}
      <section className="contact-channels-section">
        <div className="contact-channels-inner">
          {contactChannels.map((ch) => {
            const Icon = ch.icon;
            return (
              <div key={ch.title} className="contact-channel-card">
                <div className="contact-channel-icon">
                  <Icon size={22} />
                </div>
                <h3 className="contact-channel-title">{ch.title}</h3>
                <span className="contact-channel-detail">{ch.detail}</span>
                <span className="contact-channel-sub">{ch.sub}</span>
              </div>
            );
          })}
        </div>
      </section>

      {/* Form + Sidebar */}
      <section
        id="form"
        ref={(el) => (sectionRefs.current['form'] = el)}
        className={`rsc-section ${isVisible('form') ? 'contact-visible' : ''}`}
      >
        <div className="rsc-section-inner contact-form-grid">
          {/* Left — Form */}
          <div className="contact-form-wrapper">
            <h2 className="contact-form-heading">Send Us a Message</h2>
            <form className="contact-form" onSubmit={handleSubmit}>
              <div className="contact-field-row">
                <div className="contact-field">
                  <label htmlFor="c-name">Full Name</label>
                  <input
                    id="c-name"
                    name="name"
                    type="text"
                    placeholder="Jane Doe"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="contact-field">
                  <label htmlFor="c-email">Email</label>
                  <input
                    id="c-email"
                    name="email"
                    type="email"
                    placeholder="jane@university.edu"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className="contact-field">
                <label htmlFor="c-subject">Subject</label>
                <input
                  id="c-subject"
                  name="subject"
                  type="text"
                  placeholder="How can we help?"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="contact-field">
                <label htmlFor="c-message">Message</label>
                <textarea
                  id="c-message"
                  name="message"
                  rows={6}
                  placeholder="Tell us more about your question or idea…"
                  value={formData.message}
                  onChange={handleChange}
                  required
                />
              </div>

              <button type="submit" className="rsc-btn rsc-btn-primary contact-submit-btn" disabled={submitting}>
                {submitting ? 'Sending...' : 'Send Message'} {!submitting && <Send size={16} />}
              </button>
              {submitStatus.message && (
                <p className={`contact-submit-status contact-submit-status--${submitStatus.type}`}>
                  {submitStatus.message}
                </p>
              )}
            </form>
          </div>

          {/* Right — Sidebar */}
          <aside className="contact-sidebar">
            <div className="contact-sidebar-card">
              <HelpCircle size={20} className="contact-sidebar-icon" />
              <h4>Looking for quick answers?</h4>
              <p>Browse our Help Center for guides, FAQs, and troubleshooting tips.</p>
              <Link to="/help" className="rsc-btn rsc-btn-outline" style={{ width: '100%', justifyContent: 'center' }}>
                Visit Help Center <ArrowRight size={14} />
              </Link>
            </div>

            <div className="contact-sidebar-card">
              <Globe size={20} className="contact-sidebar-icon" />
              <h4>Follow Us</h4>
              <div className="contact-socials">
                {socials.map((s) => {
                  const Icon = s.icon;
                  return (
                    <a key={s.label} href={s.href} className="contact-social-link" aria-label={s.label}>
                      <Icon size={18} />
                    </a>
                  );
                })}
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* Offices */}
      <section
        id="offices"
        ref={(el) => (sectionRefs.current['offices'] = el)}
        className={`rsc-section rsc-section-alt ${isVisible('offices') ? 'contact-visible' : ''}`}
      >
        <div className="rsc-section-inner">
          <span className="rsc-section-label">Our Offices</span>
          <h2 className="rsc-section-title">Where to Find Us</h2>
          <p className="rsc-section-subtitle">
            We're remote-first with hubs across three continents.
          </p>
          <div className="rsc-grid rsc-grid-3">
            {offices.map((office) => (
              <div key={office.city} className="rsc-card contact-office-card">
                <div className="contact-office-pin">
                  <MapPin size={18} />
                </div>
                <h3 className="rsc-card-title">{office.city}</h3>
                <p className="rsc-card-text" style={{ whiteSpace: 'pre-line' }}>
                  {office.address}
                </p>
                <span className="contact-office-tz">
                  <Clock size={13} /> {office.timezone}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section
        id="contact-cta"
        ref={(el) => (sectionRefs.current['contact-cta'] = el)}
        className={`rsc-cta ${isVisible('contact-cta') ? 'rsc-cta--visible' : ''}`}
      >
        <div className="rsc-cta-inner">
          <span className="rsc-cta-badge">
            <Zap size={14} />
            Get Started
          </span>
          <h2 className="rsc-cta-title">Ready to Collaborate?</h2>
          <p className="rsc-cta-text">
            Join thousands of researchers already using ResearchPro to publish,
            connect, and discover groundbreaking research.
          </p>
          <div className="rsc-cta-actions">
            <Link to="/signup" className="rsc-btn rsc-btn--primary">
              Create Free Account
              <ArrowRight size={16} />
            </Link>
            <Link to="/features" className="rsc-btn rsc-btn--secondary">
              Explore Features
            </Link>
          </div>
        </div>
      </section>
    </CompanyLayout>
  );
};

export default ContactPage;
